import type { Directive, DirectiveBinding } from "vue";

interface ClosableValue {
  handler: string;
  exclude: string[];
}

interface ClosableElement extends HTMLElement {
  closableHandler?: (e: Event) => void;
}

type RefValue = HTMLElement | HTMLElement[] | undefined;

interface InstanceWithRefs {
  $refs: Record<string, RefValue>;
  [key: string]: unknown;
}

// Per-element handler so multiple v-closable bindings don't clobber each other
const Closable: Directive<HTMLElement, ClosableValue> = {
  beforeMount(el, binding: DirectiveBinding<ClosableValue>) {
    const target = el as ClosableElement;
    target.closableHandler = (e: Event) => {
      e.stopPropagation();
      const { handler, exclude } = binding.value;

      let clickedOnExcludedEl = false;
      const instance = binding.instance as unknown as InstanceWithRefs;
      exclude.forEach((refName: string) => {
        if (!clickedOnExcludedEl) {
          const excludedEl = instance.$refs[refName];
          if (Array.isArray(excludedEl)) {
            clickedOnExcludedEl = excludedEl.some((elRef: HTMLElement) => elRef.contains(e.target as Node));
          } else if (excludedEl) {
            clickedOnExcludedEl = excludedEl.contains(e.target as Node);
          }
        }
      });

      if (!el.contains(e.target as Node) && !clickedOnExcludedEl) {
        const handlerFn = instance[handler] as (() => void) | undefined;
        if (typeof handlerFn === "function") {
          handlerFn();
        }
      }
    };
    document.addEventListener("click", target.closableHandler);
    document.addEventListener("touchstart", target.closableHandler);
  },

  unmounted(el) {
    const target = el as ClosableElement;
    if (target.closableHandler) {
      document.removeEventListener("click", target.closableHandler);
      document.removeEventListener("touchstart", target.closableHandler);
      delete target.closableHandler;
    }
  }
};

export default Closable;
